import { useLocation, useNavigate } from "react-router-dom";
import { Search, Bell, RefreshCw, ChevronRight, Home, Shield, CheckCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { getRouteByPath } from "@/config/adminRoutes";
import { cn } from "@/lib/utils";
import { useState, useRef, useEffect } from "react";
import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { formatDistanceToNow } from "date-fns";
import { th } from "date-fns/locale";

export function AdminTopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAdminNotifications();

  const currentRoute = getRouteByPath(location.pathname);
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const breadcrumbs = pathSegments.map((segment, index) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    const route = getRouteByPath(path);
    return {
      label: route?.label || segment.charAt(0).toUpperCase() + segment.slice(1),
      path,
      isLast: index === pathSegments.length - 1,
    };
  });

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const typeIcon = (type: string) => {
    switch (type) {
      case 'kyc': return <Shield className="h-4 w-4 text-amber-500" />;
      default: return <Bell className="h-4 w-4 text-blue-500" />;
    }
  };

  const handleNotifClick = (notif: { id: string; is_read: boolean; related_url: string | null }) => {
    if (!notif.is_read) markAsRead(notif.id);
    if (notif.related_url) navigate(notif.related_url);
    setNotifOpen(false);
  };

  return (
    <header className="admin-topbar sticky top-0 z-30">
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Left: Trigger + Breadcrumbs */}
        <div className="flex items-center gap-3">
          <SidebarTrigger className="h-9 w-9 rounded-xl bg-background/60 hover:bg-background border border-border/50 shadow-sm" />
          
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Home className="h-3.5 w-3.5 text-muted-foreground" />
            {breadcrumbs.map((crumb) => (
              <div key={crumb.path} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                <span className={cn(
                  "transition-colors",
                  crumb.isLast 
                    ? "font-semibold text-foreground" 
                    : "text-muted-foreground hover:text-foreground cursor-pointer"
                )}>
                  {crumb.label}
                </span>
              </div>
            ))}
          </nav>
        </div>

        {/* Right: Search + Actions */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className={cn(
            "transition-all duration-300 overflow-hidden",
            searchOpen ? "w-64" : "w-0"
          )}>
            <Input 
              placeholder="ค้นหาเมนู..." 
              className="h-9 rounded-xl bg-background/80 border-border/50 text-sm"
              onBlur={() => setSearchOpen(false)}
              autoFocus={searchOpen}
            />
          </div>
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9 rounded-xl hover:bg-background/80"
            onClick={() => setSearchOpen(!searchOpen)}
          >
            <Search className="h-4 w-4 text-muted-foreground" />
          </Button>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-xl hover:bg-background/80 relative"
              onClick={() => setNotifOpen(!notifOpen)}
            >
              <Bell className="h-4 w-4 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[hsl(var(--admin-danger))] text-[10px] text-white flex items-center justify-center font-bold animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            {/* Dropdown */}
            {notifOpen && (
              <div className="absolute right-0 top-12 w-96 max-h-[480px] bg-popover border border-border rounded-2xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">การแจ้งเตือน</span>
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
                        {unreadCount} ใหม่
                      </Badge>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-primary hover:text-primary"
                      onClick={markAllAsRead}
                    >
                      <CheckCheck className="h-3 w-3 mr-1" />
                      อ่านทั้งหมด
                    </Button>
                  )}
                </div>

                {/* List */}
                <div className="overflow-y-auto max-h-[380px]">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                      <Bell className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-sm">ยังไม่มีการแจ้งเตือน</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <button
                        key={notif.id}
                        onClick={() => handleNotifClick(notif)}
                        className={cn(
                          "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50 border-b border-border/30 last:border-b-0",
                          !notif.is_read && "bg-primary/5"
                        )}
                      >
                        <div className="mt-0.5 p-1.5 rounded-lg bg-muted">
                          {typeIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-sm truncate",
                              !notif.is_read ? "font-semibold text-foreground" : "text-muted-foreground"
                            )}>
                              {notif.title}
                            </span>
                            {!notif.is_read && (
                              <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{notif.message}</p>
                          <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground/70">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: th })}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Refresh */}
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl hover:bg-background/80">
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </div>
    </header>
  );
}
